/**
 * Packet recipients report (Item 2a) + pre-send approval gate actions (Item 2b).
 * Every student SENT a packet for the site/session, with parent contact,
 * inline data-quality flags, and CSV export. Read-only — sends nothing.
 * Usage: /admin/packet-recipients?session=<id>
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Link,
  Chip,
} from '@mui/material';
import api, {
  getPacketRecipients,
  getPacketRecipientsCsv,
  setPacketSendApproval,
} from '../services/api';

function fmtIso(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function serviceTypeLabel(serviceType) {
  const t = (serviceType || 'unknown').replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

const DATA_QUALITY_LABELS = {
  phone_in_name: 'Phone in name',
  phone_in_email: 'Phone in email',
  invalid_email: 'Invalid email',
  missing_email: 'No email',
  email_in_phone: 'Email in phone',
  invalid_phone: 'Invalid phone',
  missing_phone: 'No phone',
};

const APPROVAL_CHIP = {
  approved: { label: 'Approved', color: 'success' },
  pending: { label: 'Pending review', color: 'warning' },
  held: { label: 'Held', color: 'error' },
};

export default function PacketRecipientsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionParam = searchParams.get('session') || '';
  const [sessionId, setSessionId] = useState(sessionParam);
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getPacketRecipients(sessionId);
      setRows(res.data.results || []);
      setMeta({
        site_id: res.data.site_id,
        site_slug: res.data.site_slug,
        review_required: !!res.data.review_required,
      });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionParam) setSessionId(sessionParam);
  }, [sessionParam]);

  useEffect(() => {
    if (sessionId) load();
  }, [sessionId, load]);

  // No session in the URL: default to the current (active) session for this site.
  useEffect(() => {
    if (sessionParam || sessionId) return;
    api.get('/sessions/current-session/')
      .then((res) => { if (res.data?.id) setSessionId(String(res.data.id)); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = async () => {
    if (!sessionId) return;
    try {
      const res = await getPacketRecipientsCsv(sessionId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'packet_recipients.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'CSV export failed');
    }
  };

  const decide = async (row, action) => {
    setApprovingId(row.student_id);
    setError('');
    try {
      const res = await setPacketSendApproval(row.student_id, action);
      setRows((prev) => prev.map((r) => (r.student_id === row.student_id ? { ...r, ...res.data } : r)));
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Approval action failed');
    } finally {
      setApprovingId(null);
    }
  };

  const nameQuery = nameFilter.trim().toLowerCase();
  const visibleRows = rows.filter((r) => {
    if (nameQuery && !`${r.last_name || ''}, ${r.first_name || ''}`.toLowerCase().includes(nameQuery)) return false;
    if (statusFilter && r.overall !== statusFilter) return false;
    if (serviceFilter && (r.service_type || 'unknown') !== serviceFilter) return false;
    const issues = (r.data_quality || []).length > 0;
    if (qualityFilter === 'issues' && !issues) return false;
    if (qualityFilter === 'clean' && issues) return false;
    if (approvalFilter && (r.approval_state || '') !== approvalFilter) return false;
    return true;
  });

  const issueCount = rows.filter((r) => (r.data_quality || []).length > 0).length;

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Packet recipients
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Session ID"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            sx={{ width: 200 }}
          />
          <Button
            variant="contained"
            onClick={() => {
              setSearchParams({ session: sessionId });
              load();
            }}
          >
            Load
          </Button>
          <Button variant="outlined" disabled={!rows.length || loading} onClick={downloadCsv}>
            Export CSV
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
          <TextField
            size="small"
            label="Student"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Name contains…"
            sx={{ width: 190 }}
          />
          <TextField
            size="small"
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ width: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
            <MenuItem value="in_progress">In progress</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Service type"
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            sx={{ width: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="center_based">Center based</MenuItem>
            <MenuItem value="related_service">Related service</MenuItem>
            <MenuItem value="seit">SEIT</MenuItem>
            <MenuItem value="unknown">Unknown</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Data quality"
            value={qualityFilter}
            onChange={(e) => setQualityFilter(e.target.value)}
            sx={{ width: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="issues">Has issues</MenuItem>
            <MenuItem value="clean">Clean</MenuItem>
          </TextField>
          {meta?.review_required && (
            <TextField
              size="small"
              select
              label="Approval"
              value={approvalFilter}
              onChange={(e) => setApprovalFilter(e.target.value)}
              sx={{ width: 170 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="held">Held</MenuItem>
            </TextField>
          )}
          {rows.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Showing {visibleRows.length} of {rows.length}
              {issueCount > 0 ? ` · ${issueCount} with data-quality issues` : ''}
            </Typography>
          )}
        </Box>
      </Paper>
      {meta && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Session site: {meta.site_slug || meta.site_id || '—'} · Students who were <strong>sent</strong> a packet
          {meta.review_required
            ? ' · Pre-send review gate is ACTIVE for this site — new sends require reviewer approval.'
            : ''}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && sessionId && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell align="right">ID</TableCell>
              <TableCell>Program</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Parent email</TableCell>
              <TableCell>Parent phone</TableCell>
              <TableCell>Data quality</TableCell>
              {meta?.review_required && <TableCell>Approval</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((r) => {
              const approvalChip = APPROVAL_CHIP[r.approval_state] || APPROVAL_CHIP.pending;
              return (
                <TableRow key={r.student_id} hover>
                  <TableCell>
                    <Link component={RouterLink} to={r.student_url_path || `/students/${r.student_id}`} underline="hover">
                      {r.last_name}, {r.first_name}
                    </Link>
                  </TableCell>
                  <TableCell align="right">{r.student_id}</TableCell>
                  <TableCell>
                    <Chip label={serviceTypeLabel(r.service_type)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{r.overall}</TableCell>
                  <TableCell align="right">{r.pct_complete != null ? `${r.pct_complete}%` : '—'}</TableCell>
                  <TableCell>{fmtIso(r.sent_at)}</TableCell>
                  <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.parent_email}>
                    {r.parent_email || '—'}
                  </TableCell>
                  <TableCell>{r.parent_phone || '—'}</TableCell>
                  <TableCell>
                    {(r.data_quality || []).length === 0
                      ? ''
                      : (r.data_quality || []).map((flag) => (
                        <Chip
                          key={flag}
                          label={DATA_QUALITY_LABELS[flag] || flag}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                  </TableCell>
                  {meta?.review_required && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={approvalChip.label}
                        size="small"
                        color={approvalChip.color}
                        variant="outlined"
                        title={
                          r.approval_decided_by_name
                            ? `${approvalChip.label} by ${r.approval_decided_by_name} on ${fmtIso(r.approval_decided_at)}`
                            : ''
                        }
                      />
                      {r.approval_state !== 'approved' && (
                        <Button
                          size="small"
                          disabled={approvingId === r.student_id}
                          onClick={() => decide(r, 'approve')}
                          sx={{ ml: 0.5 }}
                        >
                          Approve
                        </Button>
                      )}
                      {r.approval_state === 'approved' && (
                        <Button
                          size="small"
                          color="warning"
                          disabled={approvingId === r.student_id}
                          onClick={() => decide(r, 'hold')}
                          sx={{ ml: 0.5 }}
                        >
                          Hold
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
